// Pure audio-format helpers shared by the recorder. No DOM, no React,
// no axios. Safe to unit-test with plain Jest and to import from any
// layer.

// Format a number of seconds as mm:ss with leading zeros. Used by the
// single-track timer where the value is always a whole-second counter.
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Same shape as formatTime but tolerates fractional seconds and NaN /
// undefined. Used by the advanced player and the multitrack timeline
// where the value comes from audio.currentTime.
export const formatTimeSeconds = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Decode a "data:<mime>;base64,<payload>" string into the raw mime and
// a base64 payload. Returns null on a malformed input.
export const splitDataUrl = (dataUrl) => {
  if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) return null;
  const [header, payload] = dataUrl.split(',', 2);
  const m = header.match(/^data:([^;]+);base64$/);
  if (!m) return null;
  return { mimeType: m[1], base64: payload };
};

// Convert a raw base64 payload (no data:URL prefix) into an
// ArrayBuffer. The browser-native path is atob + a Uint8Array fill.
export const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const ab = new ArrayBuffer(len);
  const view = new Uint8Array(ab);
  for (let i = 0; i < len; i++) view[i] = binary.charCodeAt(i);
  return ab;
};

// Convert a raw base64 payload into a Blob with the provided mime
// type. Used to materialise saved recordings for download or for the
// HTMLAudioElement playback path.
export const base64ToBlob = (base64, mimeType = 'audio/webm') => {
  return new Blob([base64ToArrayBuffer(base64)], { type: mimeType });
};

// Convert a "data:<mime>;base64,<payload>" string straight into a
// Blob, picking the mime from the header. Returns null on malformed
// input so callers can fall through to an explicit mime override.
export const dataUrlToBlob = (dataUrl, fallbackMime = 'audio/webm') => {
  const parts = splitDataUrl(dataUrl);
  if (!parts) return null;
  return base64ToBlob(parts.base64, parts.mimeType || fallbackMime);
};
